import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  InputDefinition,
  Priority,
  ProvenanceRecord,
} from '@arcanum-hq/shared-types';
import type { ChipVariant } from '@/components/ui/Chip';
import { formatByDisplayFormat } from '@/lib/format';

export interface RankedAssumption {
  namedRange: string;
  label: string;
  /** Raw current value pulled from `inputValues[name]`. */
  rawValue: unknown;
  /** Pre-formatted display string ready for `<AssumptionCard>`. */
  formattedValue: string;
  /** 0..1 — defaults to 0 when no provenance record exists. */
  confidence: number;
  /** Best-effort proxy for IRR sensitivity (priority + affectsOutputs boost). */
  sensitivityToIRR: number;
  source: ChipVariant;
  sourceDetail?: string;
}

export interface PaginatedAssumptionsState {
  /** Current page slice — empty array when `total === 0`. */
  current: RankedAssumption[];
  page: number;
  totalPages: number;
  total: number;
  goNext: () => void;
  goPrev: () => void;
  goPage: (n: number) => void;
}

const PRIORITY_WEIGHT: Record<Priority, number> = {
  critical: 1.0,
  high: 0.7,
  medium: 0.4,
  low: 0.1,
};

const IRR_AFFECT_BOOST = 1.5;

/**
 * Best-effort proxy for "sensitivity to IRR." There is no per-input
 * numeric sensitivity field in the schema, so we approximate with:
 *
 *   weight = priorityWeight * (1.5 if affectsOutputs.includes('IRR') else 1.0)
 *
 * Captured here (vs inlined in the sort comparator) so the rationale is
 * grep-able and a future migration to a real sensitivity store has one
 * place to swap.
 */
function sensitivityProxy(input: InputDefinition): number {
  const base = PRIORITY_WEIGHT[input.priority] ?? PRIORITY_WEIGHT.medium;
  const irrBoost = input.affectsOutputs.includes('IRR')
    ? IRR_AFFECT_BOOST
    : 1.0;
  return base * irrBoost;
}

// deal-signal-overhaul Phase 4 Session 4 (2026-05-12): refined to support the
// 5-label visible badge (USER / MARKET / DEFAULT / INFERRED / —) per Lovable
// parity. `default` and `calculated` map to the `system` ChipVariant (visible
// label "DEFAULT") to distinguish them from `scenario` / `inferred` which
// stay on the `agent` ChipVariant (visible label "INFERRED"). The 5-label
// reservation BENCHMARK is unused until the `benchmark-data-seed` sister
// feature lands.
const SOURCE_TO_CHIP: Record<ProvenanceRecord['source'], ChipVariant> = {
  user: 'user',
  market_data: 'market',
  default: 'system',
  calculated: 'system',
  scenario: 'agent',
  inferred: 'agent',
};

/**
 * Format an input's raw value using the schema's `displayFormat` when
 * available. Falls back to dataType-aware defaults (percentages render
 * with 1-decimal `%`, integers as locale-grouped, decimals as 2-decimal,
 * currency as `$`-prefixed compact). The previous magnitude-based
 * heuristic mis-classified small integers (e.g. `Units = 4`) as
 * 3-decimal percentages — replaced 2026-05-08 (gap-analysis fix).
 */
function formatAssumptionValue(
  raw: unknown,
  def: Pick<InputDefinition, 'dataType' | 'displayFormat'>
): string {
  if (raw == null || raw === '') return '—';
  if (typeof raw === 'string' || typeof raw === 'boolean') return String(raw);
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return '—';

  // Honor the schema's authored format string when present (the Excel
  // ingest preserves these). `formatByDisplayFormat` already covers
  // percentage / multiplier / currency / integer / date branches.
  if (def.displayFormat && def.displayFormat.length > 0) {
    return formatByDisplayFormat(raw, def.displayFormat);
  }

  switch (def.dataType) {
    case 'percentage':
      return `${(raw * 100).toFixed(1)}%`;
    case 'currency':
    case 'currency_per_sf':
    case 'currency_per_unit_year':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(raw);
    case 'integer':
    case 'months':
    case 'years':
      return raw.toLocaleString('en-US', { maximumFractionDigits: 0 });
    case 'decimal':
      return raw.toFixed(2);
    default:
      return raw.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
}

export interface UsePaginatedAssumptionsArgs {
  inputDefinitions: InputDefinition[] | null | undefined;
  inputValues: Record<string, unknown> | null | undefined;
  inputProvenance: Record<string, ProvenanceRecord> | null | undefined;
  pageSize?: 4 | 8;
}

/**
 * Top-N weighted assumptions ranked by `(1 − confidence) × |sensitivityToIRR|`.
 * Pure data-shaping over the schema's `inputs[]` + the conversation's
 * `inputValues` + `inputProvenance` records.
 *
 * Pagination behavior:
 * - `pageSize` defaults to 4 (auth Builder Signal); public Deal Link passes 8.
 * - Out-of-range page numbers clamp to `[0, totalPages-1]` via `goPage`.
 * - Keyboard arrow handling is the parent's responsibility — the hook
 *   exposes `goPrev`/`goNext` which the parent's `useEffect` wires to
 *   `keydown`. Centralizing the keyboard handler in the hook would force
 *   it onto every consumer (incl. tests that don't render the doc).
 */
export function usePaginatedAssumptions({
  inputDefinitions,
  inputValues,
  inputProvenance,
  pageSize = 4,
}: UsePaginatedAssumptionsArgs): PaginatedAssumptionsState {
  const ranked = useMemo<RankedAssumption[]>(() => {
    if (!inputDefinitions || inputDefinitions.length === 0) return [];
    const provenance = inputProvenance ?? {};
    const values = inputValues ?? {};

    // Alias-sibling map keyed by `${sheet}!${cell}` → array of input names that
    // target that cell. The multifamily-v1 schema has 4 known alias pairs
    // (ConstrPeriod/ConstructPeriod, ExpenseIncrease/ExpensesIncrease,
    // InterestRate/SeniorInterestRate, LeaseupPerMonth/LeasesPerMonth); each
    // pair represents the SAME underlying cell. When the agent writes one
    // alias, the other alias's record/value would otherwise miss the direct
    // name-keyed lookup → render INFERRED with em-dash. Falling back through
    // siblings makes the lookup name-agnostic at the cell level.
    //
    // Surfaced by `new-development-input-roundtrip` Session 4 (2026-05-26):
    // 2/43 cards rendered INFERRED ghosts (ExpenseIncrease, LeasesPerMonth).
    // Generalized — any future model with alias pairs hits this same path.
    const aliasMap = new Map<string, string[]>();
    for (const def of inputDefinitions) {
      if (!def.sheet || !def.cell) continue; // composite (location) inputs skip
      const key = `${def.sheet}!${def.cell}`;
      const siblings = aliasMap.get(key);
      if (siblings) {
        siblings.push(def.name);
      } else {
        aliasMap.set(key, [def.name]);
      }
    }

    const resolveRecord = (
      def: InputDefinition
    ): ProvenanceRecord | undefined => {
      const direct = provenance[def.name];
      if (direct) return direct;
      if (!def.sheet || !def.cell) return undefined;
      const siblings = aliasMap.get(`${def.sheet}!${def.cell}`);
      if (!siblings || siblings.length < 2) return undefined;
      for (const siblingName of siblings) {
        if (siblingName === def.name) continue;
        const siblingRecord = provenance[siblingName];
        if (siblingRecord) return siblingRecord;
      }
      return undefined;
    };

    const resolveValue = (def: InputDefinition): unknown => {
      if (def.name in values) return values[def.name];
      if (!def.sheet || !def.cell) return undefined;
      const siblings = aliasMap.get(`${def.sheet}!${def.cell}`);
      if (!siblings || siblings.length < 2) return undefined;
      for (const siblingName of siblings) {
        if (siblingName === def.name) continue;
        if (siblingName in values) return values[siblingName];
      }
      return undefined;
    };

    const items = inputDefinitions.map<RankedAssumption>((def) => {
      const record = resolveRecord(def);
      const rawValue = resolveValue(def);
      const confidence = record ? record.confidence : 0;
      const source = record ? SOURCE_TO_CHIP[record.source] : 'agent';
      return {
        namedRange: def.name,
        label: def.displayName,
        rawValue,
        formattedValue: formatAssumptionValue(rawValue, def),
        confidence,
        sensitivityToIRR: sensitivityProxy(def),
        source,
        sourceDetail: record?.sourceDetail,
      };
    });

    items.sort((a, b) => {
      const aScore = (1 - a.confidence) * Math.abs(a.sensitivityToIRR);
      const bScore = (1 - b.confidence) * Math.abs(b.sensitivityToIRR);
      if (aScore === bScore) return a.label.localeCompare(b.label);
      return bScore - aScore;
    });

    return items;
  }, [inputDefinitions, inputValues, inputProvenance]);

  const total = ranked.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const [page, setPage] = useState(0);

  // Clamp page index when the dataset shrinks beneath the active page (e.g.
  // model-version migration drops some inputs while the user is paginated
  // beyond the new last page).
  useEffect(() => {
    if (page > totalPages - 1) setPage(Math.max(0, totalPages - 1));
  }, [page, totalPages]);

  const current = useMemo(() => {
    if (total === 0) return [];
    const start = page * pageSize;
    return ranked.slice(start, start + pageSize);
  }, [ranked, page, pageSize, total]);

  const goNext = useCallback(() => {
    setPage((p) => Math.min(p + 1, totalPages - 1));
  }, [totalPages]);
  const goPrev = useCallback(() => {
    setPage((p) => Math.max(p - 1, 0));
  }, []);
  const goPage = useCallback(
    (n: number) => {
      if (!Number.isFinite(n)) return;
      setPage(Math.min(Math.max(0, Math.trunc(n)), totalPages - 1));
    },
    [totalPages]
  );

  return { current, page, totalPages, total, goNext, goPrev, goPage };
}
